/**
 * What each data-quality finding SHOWS when an operator opens it.
 *
 * The counts on the data-quality tab were never the whole story: the revenue
 * analytics response already carries the affected records in full for 32 of its
 * 34 findings, uncapped, and the tab threw them away. This file is the projection
 * that puts them on screen.
 *
 * ORGANISED BY REF FAMILY, NOT BY FINDING.
 * The findings are served by ten record shapes — a tenancy reference, a revenue
 * row, an overlap pair, a ledger row, a duplicate group, and five invoice/payment
 * shapes from the attribution, aging, provider-presence and cashflow readers. Ten
 * field lists cover all of them, and a finding names its family. A per-finding
 * field list for each would be thirty-five places for the same column to drift,
 * and most of them would be identical.
 *
 * NOTHING HERE DERIVES A VALUE.
 * Every `get` reads a key the response already carries and formats it. There is
 * no subtraction (`difference` and the aging remainders arrive computed), no
 * division, no summation, and no currency assumption. A field whose source is
 * absent returns null and is left out entirely rather than printing an empty cell
 * that reads like a missing value instead of an inapplicable one — the rule
 * `bexioWarningFields` established for the same job on the reconciliation side.
 *
 * TWO DATES, AND ONLY ONE OF THEM IS A PERIOD.
 * `rent_period` on a ledger row IS the authoritative rent month: it is the field
 * the whole attribution contract reads, machine-written and CHECK-constrained to
 * the first of a month. `due_date` and `payment_date` are NOT periods and are
 * labelled for what they are ("Fällig am", "Zahlungsdatum"). `issue_date` is
 * absent from every ref in this file and is never requested — deriving a rent
 * month from an invoice date is the one inference the contract forbids outright.
 */
import {
  INVOICE_STATUS_LABEL,
  PROVIDER_MISSING_LABEL,
  currencyLabel,
  fmtInvoiceMoney,
  mapLabel,
} from "../../../components/admin/bexio/bexioFormat";
import { formatDateOnly, formatDateTime } from "../../../utils/datetime";
import { revenueTypeLabelForDisplay } from "../../../utils/tenancyRevenueBreakdown";
import { fmtMoney, MONTHS_DE } from "./revenueAnalyticsFormat";

/** A UUID an operator has to recognise rather than read aloud. */
export function shortId(value) {
  const s = String(value ?? "");
  if (!s) return null;
  return s.length > 12 ? `${s.slice(0, 8)}…` : s;
}

const text = (v) => {
  const s = v == null ? "" : String(v);
  return s.trim() === "" ? null : s;
};

const int = (v) => (v == null || v === "" ? null : String(v));

/** A date-typed wire value (`YYYY-MM-DD`), through the canonical formatter. */
const day = (v) => (v ? formatDateOnly(v) : null);

/**
 * An INSTANT (`...Z`), through the canonical formatter, in Zurich wall-clock.
 *
 * Deliberately not `day`: `provider_missing_at` records the moment an inventory
 * ran, and an inventory that ran at 00:30 Zurich is a different fact from the date
 * alone — dropping the time would make two runs on the same night indistinguishable.
 */
const instant = (v) => (v ? formatDateTime(v) : null);

/**
 * A ledger row's `rent_period` as a month.
 *
 * Splits the `YYYY-MM-DD` string — no `Date`, no timezone, matching
 * `formatDateOnly`'s approach for date-typed fields. This is a FORMATTING step on
 * the authoritative period field, not a derivation: the value being read is the
 * ledger's own `rent_period`, which is what proves the month in the first place.
 */
export function rentPeriodLabel(value) {
  const s = String(value ?? "");
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(s);
  if (!m) return text(s);
  const monthName = MONTHS_DE[Number(m[2]) - 1];
  return monthName ? `${monthName} ${m[1]}` : s;
}

/** Bexio invoice status, through the shared German vocabulary. */
const invoiceStatus = (v) => (v ? mapLabel(INVOICE_STATUS_LABEL, v) : null);

/**
 * Ledger lifecycle status.
 *
 * Deliberately NOT `rowStatusFromLedger` from `bexioDraftLabels`: that helper
 * collapses `creating` and `abandoned` into one "needs review" bucket, which is
 * right for the drafts workspace and wrong here — the attribution findings exist
 * precisely because those states differ, and two of them are invariant
 * violations. An unknown value passes through verbatim rather than being dressed
 * up as one of the five.
 */
export const LEDGER_STATUS_LABEL = {
  pending: "Ausstehend",
  creating: "Erstellung angestossen",
  created: "Erstellt",
  failed: "Fehlgeschlagen",
  abandoned: "Abgebrochen",
};

const ledgerStatus = (v) => (v ? LEDGER_STATUS_LABEL[v] || String(v) : null);

/** `invoice_kind` — CHECK-pinned to `rent_month` today. Unknown passes through. */
const INVOICE_KIND_LABEL = { rent_month: "Mietmonat" };
const invoiceKind = (v) => (v ? INVOICE_KIND_LABEL[v] || String(v) : null);

/** How a ledger row came to hold its Bexio id. */
const RECOVERY_SOURCE_LABEL = {
  api_reference_match: "Über API-Referenz zugeordnet",
  manual: "Manuell",
};
const recoverySource = (v) => (v ? RECOVERY_SOURCE_LABEL[v] || String(v) : null);

/**
 * Revenue frequency.
 *
 * Deliberately NOT `revenueFrequencyLabel` from `tenancyRevenueBreakdown`: that
 * function returns "Monatlich" for EVERYTHING that is not `one_time`, which is
 * harmless where only the two cases occur and actively false here — the
 * `frequency_not_monetized` finding exists because a row carries something else,
 * and printing "Monatlich" over a `quarterly` would hide the very thing the
 * operator opened the drawer to see. Unknown values are shown raw.
 */
const FREQUENCY_LABEL = {
  monthly: "Monatlich",
  yearly: "Jährlich",
  one_time: "Einmalig",
};
const frequency = (v) => (v ? FREQUENCY_LABEL[v] || String(v) : null);

/** A per-invoice amount, with the invoice's own possibly-unknown currency. */
const invoiceMoney = (v, currency, { marker = false } = {}) =>
  v == null || v === "" ? null : fmtInvoiceMoney(v, currency, { marker });

/** A figure the contract declares CHF. */
const chf = (v) => (v == null || v === "" ? null : fmtMoney(v));

/**
 * The ten record shapes, each with the fields to show in reading order.
 *
 * `title` is the primary identifier — what the operator looks up in Bexio or in
 * Vantio. `fields` are the supporting values. `nested` names a child array that
 * gets its own family (only the duplicate group has one).
 *
 * `showsInvoices` marks the families whose records are Bexio invoices or
 * payments, so the drawer can offer the ONE navigation target that exists for
 * them. There is no route for a single Bexio invoice, so no record links
 * anywhere by itself.
 */
export const REF_FAMILIES = {
  /** ExpectedTenancyRef — a contract, named by its reference number. */
  tenancy: {
    title: (i) => i?.tenancy_reference_number || shortId(i?.tenancy_id) || "Mietverhältnis",
    fields: [
      { label: "Referenz", get: (i) => text(i.tenancy_reference_number) },
      // Technical identifiers, labelled as such: this ref carries no tenant name
      // and inventing one from an id is not available to this layer.
      { label: "Mietverhältnis-ID", get: (i) => shortId(i.tenancy_id) },
      { label: "Einheit-ID", get: (i) => shortId(i.unit_id) },
    ],
  },

  /** ExpectedRevenueRowRef — one `tenancy_revenue` row. */
  revenueRow: {
    title: (i) => revenueTypeLabelForDisplay(i?.type) || shortId(i?.revenue_row_id) || "Revenue-Zeile",
    fields: [
      { label: "Revenue-Typ", get: (i) => text(revenueTypeLabelForDisplay(i.type)) },
      { label: "Frequenz", get: (i) => frequency(i.frequency) },
      // `amount_chf` is CHF by contract and by column name.
      { label: "Betrag", get: (i) => chf(i.amount_chf) },
      { label: "Von", get: (i) => day(i.start_date) },
      { label: "Bis", get: (i) => day(i.end_date) },
      { label: "Mietverhältnis-ID", get: (i) => shortId(i.tenancy_id) },
      { label: "Zeilen-ID", get: (i) => shortId(i.revenue_row_id) },
    ],
  },

  /** ExpectedOverlapRef — two rows of the same type whose windows overlap. */
  overlap: {
    title: (i) => revenueTypeLabelForDisplay(i?.type) || "Überlappung",
    fields: [
      { label: "Revenue-Typ", get: (i) => text(revenueTypeLabelForDisplay(i.type)) },
      { label: "Zeile A", get: (i) => shortId(i.first_revenue_row_id) },
      { label: "Zeile B", get: (i) => shortId(i.second_revenue_row_id) },
      { label: "Mietverhältnis-ID", get: (i) => shortId(i.tenancy_id) },
    ],
  },

  /** PeriodAttributionLedgerRef — one draft-create ledger row. */
  ledger: {
    title: (i) =>
      i?.bexio_invoice_id != null
        ? `Bexio #${i.bexio_invoice_id}`
        : `Anfrage ${shortId(i?.request_id) || "—"}`,
    fields: [
      // The authoritative rent month. This is the field the whole attribution
      // contract reads; it is not derived from any invoice date.
      { label: "Mietmonat", get: (i) => rentPeriodLabel(i.rent_period) },
      { label: "Art", get: (i) => invoiceKind(i.invoice_kind) },
      { label: "Ledger-Status", get: (i) => ledgerStatus(i.status) },
      { label: "Bexio-ID", get: (i) => int(i.bexio_invoice_id) },
      { label: "Herkunft", get: (i) => recoverySource(i.recovery_source) },
      { label: "Mietverhältnis-ID", get: (i) => shortId(i.tenancy_id) },
      { label: "Anfrage-ID", get: (i) => shortId(i.request_id) },
    ],
    showsInvoices: true,
  },

  /** PeriodAttributionDuplicateGroup — one invoice claimed by several rows. */
  duplicateGroup: {
    title: (i) => `Bexio #${i?.bexio_invoice_id ?? "—"}`,
    fields: [
      {
        label: "Beanspruchende Ledger-Einträge",
        get: (i) => (Array.isArray(i.ledger_rows) ? String(i.ledger_rows.length) : null),
      },
    ],
    // The rows themselves, so a human can see WHICH ones disagree. No row is
    // marked as the correct one: the database guarantees no ordering and this is
    // a diagnosis, not a repair.
    nested: { key: "ledger_rows", family: "ledger", label: "Beanspruchende Einträge" },
    showsInvoices: true,
  },

  /** PeriodAttributionInvoiceRef — a mirrored invoice, from the attribution side. */
  attributionInvoice: {
    title: (i) =>
      i?.document_nr || (i?.bexio_invoice_id != null ? `Bexio #${i.bexio_invoice_id}` : "Rechnung"),
    fields: [
      { label: "Status", get: (i) => invoiceStatus(i.bexio_status) },
      { label: "Währung", get: (i) => currencyLabel(i.currency) },
      { label: "Fakturiert", get: (i) => invoiceMoney(i.invoiced_total, i.currency) },
      { label: "Bezahlt", get: (i) => invoiceMoney(i.paid_current, i.currency) },
      { label: "Offen", get: (i) => invoiceMoney(i.open_current, i.currency) },
      { label: "Bexio-ID", get: (i) => int(i.bexio_invoice_id) },
      // The MANUAL mirror mapping's tenancy, present for diagnosis only. It never
      // decides a period, so it is labelled as a mapping and not as "the tenant".
      { label: "Zuordnung (Mapping)", get: (i) => shortId(i.mirror_tenancy_id) },
    ],
    showsInvoices: true,
  },

  /** AgingInvoiceRef — an open receivable. */
  agingInvoice: {
    title: (i) =>
      i?.document_nr || (i?.bexio_invoice_id != null ? `Bexio #${i.bexio_invoice_id}` : "Rechnung"),
    fields: [
      { label: "Status", get: (i) => invoiceStatus(i.bexio_status) },
      { label: "Währung", get: (i) => currencyLabel(i.currency) },
      { label: "Betrag", get: (i) => invoiceMoney(i.total, i.currency) },
      { label: "Bezahlt", get: (i) => invoiceMoney(i.total_paid, i.currency) },
      { label: "Offener Rest", get: (i) => invoiceMoney(i.total_remaining, i.currency) },
      // An aging date, and labelled as one. It is NOT a rent month and the
      // receivables block makes no period claim at all.
      { label: "Fällig am", get: (i) => day(i.due_date) },
      { label: "Bexio-ID", get: (i) => int(i.bexio_invoice_id) },
    ],
    showsInvoices: true,
  },

  /**
   * BexioInvoiceRead, filtered to `provider_presence=missing`.
   *
   * A SEPARATE FAMILY FROM `agingInvoice`, although both describe a mirrored
   * invoice. Two of the fields here do not exist on an aging ref at all — the
   * provider-presence timestamps — and the amounts mean something different: an
   * aging ref's "Offener Rest" IS a current receivable, while this one is the last
   * figure the mirror recorded before a completed inventory stopped finding the
   * invoice. Reusing the aging family would have printed the second under the
   * first's meaning.
   *
   * `Betrag` and `Offener Rest` keep their exact labels rather than gaining a
   * "(historisch)" suffix: the drawer states that once, in the finding's own
   * description, where it is read before any row — and the guard that pins every
   * money field to a currency field beside it matches labels exactly, so a
   * decorated label would have quietly excused this family from it.
   */
  providerMissingInvoice: {
    title: (i) =>
      i?.document_nr || (i?.bexio_invoice_id != null ? `Bexio #${i.bexio_invoice_id}` : "Rechnung"),
    fields: [
      // The MIRRORED status, unchanged. Provider-presence is a second, orthogonal
      // fact: an invoice can be "bezahlt" at the last sync AND absent from the next
      // full inventory. Overwriting the status with the presence would destroy the
      // history the mirror is kept for.
      { label: "Status", get: (i) => invoiceStatus(i.bexio_status) },
      // Always present for this family — every row here IS marked. A constant
      // rather than a derived value: the filter already decided it, and re-deriving
      // it from `provider_missing_at` in the projection would be a second opinion.
      { label: "Bexio-Verfügbarkeit", get: () => PROVIDER_MISSING_LABEL },
      { label: "Währung", get: (i) => currencyLabel(i.currency) },
      { label: "Betrag", get: (i) => invoiceMoney(i.total, i.currency) },
      { label: "Offener Rest", get: (i) => invoiceMoney(i.total_remaining, i.currency) },
      // Same labels the Bexio workspace drawer uses for the same two columns.
      { label: "Erkannt am", get: (i) => instant(i.provider_missing_at) },
      // Rendered ONLY when a completed inventory actually confirmed the invoice.
      // NULL means no such confirmation exists — not that the invoice was never at
      // Bexio — so `visibleRefFields` drops the field rather than printing a
      // sentence the data cannot support.
      { label: "Zuletzt in Bexio bestätigt", get: (i) => instant(i.provider_last_seen_at) },
      { label: "Bexio-ID", get: (i) => int(i.bexio_invoice_id) },
    ],
    showsInvoices: true,
  },

  /** CashflowInvoiceRef — an invoice judged as a whole, with both sides. */
  cashflowInvoice: {
    title: (i) =>
      i?.document_nr || (i?.bexio_invoice_id != null ? `Bexio #${i.bexio_invoice_id}` : "Rechnung"),
    fields: [
      { label: "Status", get: (i) => invoiceStatus(i.bexio_status) },
      { label: "Währung", get: (i) => currencyLabel(i.currency) },
      { label: "Bexio total_paid", get: (i) => invoiceMoney(i.total_paid, i.currency) },
      { label: "Summe Zahlungszeilen", get: (i) => invoiceMoney(i.payment_sum, i.currency) },
      // Signed and BACKEND-COMPUTED. `total_paid - payment_sum` is one character
      // away from being written here and would become a second source of truth.
      { label: "Differenz", get: (i) => invoiceMoney(i.difference, i.currency) },
      { label: "Zahlungszeilen", get: (i) => int(i.payment_rows) },
      { label: "Bexio-ID", get: (i) => int(i.bexio_invoice_id) },
    ],
    showsInvoices: true,
  },

  /** CashflowPaymentRef — one mirrored payment row. */
  cashflowPayment: {
    title: (i) =>
      i?.bexio_payment_id != null ? `Zahlung #${i.bexio_payment_id}` : "Zahlung",
    fields: [
      { label: "Zahlungsdatum", get: (i) => day(i.payment_date) },
      { label: "Währung", get: (i) => currencyLabel(i.currency) },
      // An orphan payment has no invoice and therefore no currency of its own.
      // The marker is OFF because this row states its currency in the field
      // directly above — which is exactly the case `fmtInvoiceMoney`'s
      // `marker: false` is documented for. The adjacency that makes the bare form
      // honest is not left to chance: a guard pins that every family rendering a
      // money field also renders its "Währung".
      { label: "Betrag", get: (i) => invoiceMoney(i.value, i.currency) },
      { label: "Rechnung", get: (i) => text(i.document_nr) },
      { label: "Rechnungsstatus", get: (i) => invoiceStatus(i.bexio_status) },
      { label: "Bexio-Rechnungs-ID", get: (i) => int(i.bexio_invoice_id) },
      { label: "Zahlungs-ID", get: (i) => int(i.bexio_payment_id) },
    ],
    showsInvoices: true,
  },
};

/** The record's primary identifier, for its family. */
export function refTitle(family, item) {
  const spec = REF_FAMILIES[family];
  if (!spec) return "—";
  return spec.title(item) || "—";
}

/** Fields that actually have a value, for this family and this record. */
export function visibleRefFields(family, item) {
  const spec = REF_FAMILIES[family];
  if (!spec || !item) return [];
  return spec.fields
    .map((f) => ({ label: f.label, value: f.get(item) }))
    .filter((f) => f.value != null && f.value !== "");
}

/** The child collection a family carries, if any. */
export function nestedSpec(family) {
  return REF_FAMILIES[family]?.nested || null;
}

/** Does this family describe Bexio invoices or payments? */
export function familyShowsInvoices(family) {
  return REF_FAMILIES[family]?.showsInvoices === true;
}
